import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import CsvUploadModal from "../uploadCSV/uploadCSV";
import UploadIntro from "../uploadIntro/uploadIntro";

const LIFELINE_FIELDS = [
  { key: "fifty_fifty", defaultName: "50:50", defaultIcon: "/lifelines/50.png" },
  { key: "expert", defaultName: "Expert Advice", defaultIcon: "/lifelines/expert-advice.png" },
  { key: "double", defaultName: "Double Dip", defaultIcon: "/lifelines/double-dip.png" },
  { key: "flip", defaultName: "Flip", defaultIcon: "/lifelines/flip.png" },
];

const LIFELINE_IMAGE_EXT = ["jpg", "jpeg", "png", "svg", "webp"];
const LIFELINE_MAX_SIZE_MB = 2;

// Game fonts selectable from the admin. Values map to @font-face family names
// declared in index.css. The admin panel itself always stays FiraSans-Medium.
const FONT_OPTIONS = [
  { value: "FiraSans-Medium", label: "Fira Sans (default)" },
  { value: "Georama", label: "Georama" },
];
const DEFAULT_FONT = "FiraSans-Medium";

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  // right‐side: page metadata
  const [mainTitle, setMainTitle] = useState("");
  const [landingTitle, setLandingTitle] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Game font (applied only in the game, not the admin panel).
  const [font, setFont] = useState(DEFAULT_FONT);

  const [colors, setColors] = useState({
    text_color: "#000000",
    ui_color_1: "#222222",
    ui_color_2: "#222222",
    option_color: "#ff9900",
    option_text_color: "#ffffff",
    panel_color: "#fffbdd",
    center_panel_color: "#ffffff",
    button_color: "#6b91e8",
  });

  useEffect(() => {
    if (!data?.colors) return;
    setColors(data.colors);
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // --- lifeline name/image customization ---
  const [lifelineNames, setLifelineNames] = useState(() =>
    LIFELINE_FIELDS.reduce(
      (acc, { key, defaultName }) => ({ ...acc, [key]: defaultName }),
      {}
    )
  );
  const [lifelinePreviews, setLifelinePreviews] = useState(() =>
    LIFELINE_FIELDS.reduce(
      (acc, { key, defaultIcon }) => ({ ...acc, [key]: defaultIcon }),
      {}
    )
  );
  const [lifelineFiles, setLifelineFiles] = useState({});
  const lifelineFileInputs = useRef({});

  useEffect(() => {
    if (!data) return;
    const names = {};
    const previews = {};
    LIFELINE_FIELDS.forEach(({ key, defaultName, defaultIcon }) => {
      names[key] = data.lifelineNames?.[key] || defaultName;
      previews[key] = data.lifelineImages?.[key]
        ? process.env.REACT_APP_S3_PATH + data.lifelineImages[key]
        : defaultIcon;
    });
    setLifelineNames(names);
    setLifelinePreviews(previews);
    setLifelineFiles({});
  }, [data]);

  const handleLifelineNameChange = (key, val) => {
    let newVal = val;
    if (val.length > 30) {
      Swal.fire(
        "Limit Exceeded",
        "Lifeline name can be at most 30 characters.",
        "warning"
      );
      newVal = val.slice(0, 30);
    }
    setLifelineNames((prev) => ({ ...prev, [key]: newVal }));
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const validateLifelineImage = async (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!LIFELINE_IMAGE_EXT.includes(ext))
      return `Only ${LIFELINE_IMAGE_EXT.join(", ")} formats allowed`;
    if (file.size > LIFELINE_MAX_SIZE_MB * 1024 * 1024)
      return `File size exceeds ${LIFELINE_MAX_SIZE_MB}MB limit`;
    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== 300 || height !== 300) {
        return "Image must be exactly 300x300px";
      }
    } catch {
      return "Failed to validate image dimensions";
    }
    return null;
  };

  const handleLifelineImageChange = async (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const error = await validateLifelineImage(file);
    if (error) {
      await Swal.fire({ title: "Invalid Image", text: error, icon: "error" });
      e.target.value = "";
      return;
    }
    setLifelineFiles((prev) => ({ ...prev, [key]: file }));
    setLifelinePreviews((prev) => ({
      ...prev,
      [key]: URL.createObjectURL(file),
    }));
  };

  const handleLifelineSubmit = async () => {
    const changedNames = {};
    LIFELINE_FIELDS.forEach(({ key, defaultName }) => {
      const original = data?.lifelineNames?.[key] || defaultName;
      if (lifelineNames[key] !== original) {
        changedNames[key] = lifelineNames[key];
      }
    });
    const hasFiles = Object.values(lifelineFiles).some(Boolean);

    if (Object.keys(changedNames).length === 0 && !hasFiles) {
      Swal.fire(
        "Nothing to Update",
        "Change a lifeline name or upload an image first.",
        "info"
      );
      return;
    }

    const formData = new FormData();
    formData.append("currentTheme", currentTheme);
    if (Object.keys(changedNames).length > 0) {
      formData.append("lifelineNames", JSON.stringify(changedNames));
    }
    LIFELINE_FIELDS.forEach(({ key }) => {
      if (lifelineFiles[key]) {
        formData.append(key, lifelineFiles[key]);
      }
    });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/updateLifelineSettings`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        }
      );
      if (!res.ok) throw new Error();
      const result = await res.json();
      await Swal.fire(
        "Success",
        result.message || "Lifeline settings updated successfully",
        "success"
      );
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (e) {
      Swal.fire(
        "Error",
        "Failed to update lifeline settings. Please try again.",
        "error"
      );
    }
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(processQuestions());
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate both rules + metadata
  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setMainTitle(data.main_title || "");
    setLandingTitle(data.landing_page_title || "");
    setThankYouText(data.custom_text_thank_you_page || "");
    // Validate stored font; fall back to default if missing/unknown.
    const stored = data.font;
    setFont(
      FONT_OPTIONS.some((o) => o.value === stored) ? stored : DEFAULT_FONT
    );
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 100) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 100 characters. extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 100);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleMainTitleChange = (val) => {
    let newVal = val;
    if (val.length > 150) {
      Swal.fire(
        "Limit Exceeded",
        "Main Title can be at most 150 characters.",
        "warning"
      );
      newVal = val.slice(0, 150);
    }
    setMainTitle(newVal);
  };

  const handleLandingTitleChange = (val) => {
    let newVal = val;
    if (val.length > 250) {
      Swal.fire(
        "Limit Exceeded",
        "Sub Title can be at most 250 characters.",
        "warning"
      );
      newVal = val.slice(0, 250);
    }
    setLandingTitle(newVal);
  };

  const handleThankYouTextChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank You Text can be at most 300 characters.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setThankYouText(newVal);
  };

  // --- submit all ---
  const handleSubmit = async () => {
    const payload = {
      data: {
        rules: rules.filter((r) => r.trim() !== ""),
        main_title: mainTitle,
        landing_page_title: landingTitle,
        custom_text_thank_you_page: thankYouText,
        colors,
        // Persist a validated font value only.
        font: FONT_OPTIONS.some((o) => o.value === font) ? font : DEFAULT_FONT,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Success", "Data saved successfully!", "success");
      setTimeout(() => navigate("/admin/questions"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <UploadIntro
        isOpen={isIntroModelOpen}
        onClose={() => setisIntroModelOpen(false)}
      />

          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i class="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="blue-tabs-container">
          <div className="blue-tabs blue-tabs-active"></div>
          <div className="blue-tabs"></div>
          <div className="blue-tabs"></div>
        </div>

        <div className="admin-tabs-holder">
          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
            </div>
            <div className="form-group-v1">
              <label>Main Title for Rules:</label>
              <input
                type="text"
                value={mainTitle}
                onChange={(e) => handleMainTitleChange(e.target.value)}
                placeholder="Main title for rules"
              />
              <div className="code">
                * Maximum character limit should be 150 characters.
              </div>
            </div>
            <div className="form-group-v1">
              <label>Add Sub Title:</label>
              <input
                type="text"
                value={landingTitle}
                onChange={(e) => handleLandingTitleChange(e.target.value)}
              />
              <div className="code">
                * Maximum character limit should be 250 characters.
              </div>
            </div>
            <div className="form-group-v1">
              <label>Thank you page text:</label>
              <input
                type="text"
                value={thankYouText}
                onChange={(e) => handleThankYouTextChange(e.target.value)}
              />
              <div className="code">
                * Maximum character limit should be 300 characters.
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-trophy"></i> &nbsp; Game Rules
            </div>
            <h3 className="custom-rules-title">Custom rules</h3>
            {rules.map((rule, i) => (
              <div className="rule-row" key={i}>
                <span className="rule-number">{i + 1}.</span>
                <input
                  className="rule-input"
                  type="text"
                  placeholder="Enter rule"
                  value={rule}
                  onChange={(e) => handleRuleChange(i, e.target.value)}
                />
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveRule(i)}
                >
                  &times;
                </button>
              </div>
            ))}
            <div className="rules-button-container">
              <div>
                <button className="add-btn" onClick={handleAddRule}>
                  + Add New Rule
                </button>
              </div>
              <div className="code">
                * Keep 6 rules at the most. <br></br>* At least 1 rule is
                required.<br></br>* character limit of each rule is 100<br></br>
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-palette"></i> &nbsp; Color Customization
            </div>
            <div className="pallet-holder">
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.text_color}
                      onChange={handleColorChange("text_color")}
                    />
                    <label>Text Color</label>
                  </div>
                  <div className="color-code">{colors.text_color}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.ui_color_2}
                      onChange={handleColorChange("ui_color_2")}
                    />
                    <label>Progress bar Color</label>
                  </div>
                  <div className="color-code">{colors.ui_color_2}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.ui_color_1}
                      onChange={handleColorChange("ui_color_1")}
                    />
                    <label>Question Color</label>
                  </div>
                  <div className="color-code">{colors.ui_color_1}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.option_color}
                      onChange={handleColorChange("option_color")}
                    />
                    <label>Option Color</label>
                  </div>
                  <div className="color-code">{colors.option_color}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.option_text_color}
                      onChange={handleColorChange("option_text_color")}
                    />
                    <label>Option Text Color</label>
                  </div>
                  <div className="color-code">{colors.option_text_color}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.panel_color || "#fffbdd"}
                      onChange={handleColorChange("panel_color")}
                    />
                    <label>Panel Color</label>
                  </div>
                  <div className="color-code">{colors.panel_color || "#fffbdd"}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.center_panel_color || "#ffffff"}
                      onChange={handleColorChange("center_panel_color")}
                    />
                    <label>Center Panel Color</label>
                  </div>
                  <div className="color-code">{colors.center_panel_color || "#ffffff"}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.button_color || "#6b91e8"}
                      onChange={handleColorChange("button_color")}
                    />
                    <label>Button Color</label>
                  </div>
                  <div className="color-code">{colors.button_color || "#6b91e8"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-life-ring"></i> &nbsp; Lifeline Customization
            </div>
            <div className="code" style={{ marginBottom: "10px" }}>
              * Optional. Replace the name and/or icon of a lifeline. Leave
              unchanged to keep the default.
            </div>
            <div className="lifeline-custom-holder">
              {LIFELINE_FIELDS.map(({ key, defaultName }) => (
                <div className="lifeline-custom-row" key={key}>
                  <div
                    className="lifeline-custom-icon-container"
                    onClick={() => lifelineFileInputs.current[key].click()}
                  >
                    <img
                      src={lifelinePreviews[key]}
                      alt={`${defaultName} icon`}
                      className="lifeline-custom-icon-preview"
                    />
                    <span className="lifeline-custom-icon-overlay">
                      <i class="fas fa-upload"></i>
                    </span>
                  </div>
                  <input
                    ref={(el) => (lifelineFileInputs.current[key] = el)}
                    type="file"
                    accept="image/jpeg,image/png,image/svg+xml,image/webp"
                    style={{ display: "none" }}
                    onChange={(e) => handleLifelineImageChange(key, e)}
                  />
                  <div className="lifeline-custom-name">
                    <label>{defaultName} name</label>
                    <input
                      type="text"
                      value={lifelineNames[key] || ""}
                      onChange={(e) =>
                        handleLifelineNameChange(key, e.target.value)
                      }
                      placeholder={defaultName}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="code">
              * Image formats allowed: jpg, jpeg, png, svg, webp. Maximum
              size 2MB. Image dimensions must be exactly 300x300px.
            </div>
            <div className="rules-action-holder">
              <button
                className="save-and-continue"
                onClick={handleLifelineSubmit}
              >
                Save Lifeline Settings
              </button>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-font"></i> &nbsp; Font Customization
            </div>
            <div className="code" style={{ marginBottom: "10px" }}>
              * Applies to the game only. The admin panel always uses the
              default font.
            </div>
            <div className="form-group-v1">
              <label>Game Font:</label>
              <select
                value={font}
                onChange={(e) => {
                  const val = e.target.value;
                  setFont(
                    FONT_OPTIONS.some((o) => o.value === val)
                      ? val
                      : DEFAULT_FONT
                  );
                }}
                style={{ fontFamily: font }}
              >
                {FONT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="code">
                * Saved when you click "Save &amp; Continue" below.
              </div>
            </div>
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save &amp; Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
